import { accentSoftWash, buttonGradient } from '@yourstore/domain';
import type { StaffBranding } from './wire';

/**
 * The staff PWA's theme, applied — **rung 2 plus the named set**
 * (`docs/lovespa-personality.md`; spec §6.4.3's staff sentence, as that document
 * resolves it).
 *
 * ## The one rule this file exists to keep
 *
 * **A tenant with no `branding_staff` gets today's look, byte for byte.** Not
 * "very nearly", not "the same colours" — the same DOM and the same computed
 * styles, with no custom property written, no class added, no element inserted.
 * That is why every write below is guarded and why `applyStaffBranding(root,
 * undefined)` *removes* what a previous call set: a session that stops carrying
 * branding (an operator cleared it; a device signed into a second tenant) has to
 * land back on the default, without a reload, on a counter iPad nobody is going
 * to restart.
 *
 * `views.test.tsx`'s sibling asserts that sentence by rendering the app with and
 * without a branding document and comparing the markup, because a promise about
 * bytes is only worth what a test makes of it.
 *
 * ## Why custom properties and not a stylesheet
 *
 * `styles.css` already reads every colour from `:root` (`--ink`, `--accent`,
 * `--paper`…), so a tenant's accent is a property write and nothing else. There
 * is no tenant-authored CSS on this surface and there is not going to be one —
 * that is rung 4, it exists on the *guest* site where a page is a brochure, and
 * the argument against it here is that a support engineer must be able to
 * recognise a screenshot of a screen a staff member is describing over the
 * phone.
 *
 * ## The background pair
 *
 * LoveSpa paints the photograph on `body::before` — fixed, oversized by 18px to
 * hide blur fringing, `center / cover` over the cream, with a media query
 * swapping the file in landscape. This bundle carries that rule in `styles.css`
 * behind two custom properties, so the orientation swap is CSS (where it
 * belongs, and where it costs nothing on rotate) and only the two URLs come from
 * config. A tenant that sets one and not the other gets that one in both
 * orientations, which is better than a blank ground in whichever way the iPad is
 * being held.
 */

/** Every property this module owns. Removed together, so a rollback is total. */
const PROPERTIES = [
  '--accent',
  '--accent-soft',
  '--paper',
  '--brand-font-display',
  '--brand-background-portrait',
  '--brand-background-landscape',
  '--button-fill-from',
  '--button-fill-to',
  '--button-fill-ink',
] as const;

/**
 * The app's own accent and ink, as `styles.css`'s `:root` states them — what
 * the button gradient is derived from when a tenant chose a fill but not an
 * accent. Restated rather than read back from computed style because a detached
 * node in a test has none.
 */
const DEFAULT_ACCENT = '#1f6f8b';
const INK = '#17212b';

/** The serif LoveSpa set on headings and the wordmark, stack for stack. */
export const DISPLAY_SERIF = "Georgia, 'Palatino Linotype', Palatino, serif";

/**
 * `true` when the document carries at least one thing worth painting.
 *
 * A `branding_staff` of `{}` — which `staffBrandingOf` never produces, but a
 * hand-edited row could — is not branding, and treating it as branding would put
 * a `data-branded` attribute on `<html>` for a tenant who chose nothing.
 */
export function hasStaffBranding(branding: StaffBranding | undefined): boolean {
  if (branding === undefined) return false;
  return Object.values(branding).some((value) => value !== undefined && value !== '');
}

/**
 * Write (or clear) the named set on an element — `document.documentElement` in
 * the app, a detached node in a test.
 *
 * Mirrors `apps/site`'s `applyTheme` deliberately, down to removing a property
 * whose value is empty rather than writing `""`: an empty custom property is not
 * the same as an absent one, because `var(--paper, #f6f7f9)` skips its fallback
 * when the property exists and is empty.
 */
export function applyStaffBranding(root: HTMLElement, branding: StaffBranding | undefined): void {
  for (const property of PROPERTIES) root.style.removeProperty(property);
  delete root.dataset['branded'];
  delete root.dataset['buttons'];

  if (!hasStaffBranding(branding) || branding === undefined) return;

  set(root, '--accent', branding.accent);
  // Derived, never configured — `accentSoftWash` in `@yourstore/domain` says why
  // there is one colour field here and two in LoveSpa's stylesheet.
  set(root, '--accent-soft', accentSoftWash(nonEmpty(branding.accent)) ?? undefined);
  set(root, '--paper', branding.ground);

  if (branding.font_display === 'serif') set(root, '--brand-font-display', DISPLAY_SERIF);

  // `url("…")`, assembled here rather than in the stylesheet, for the reason
  // `apps/site`'s `imageValue` gives: the caller should not be building strings,
  // and the value has already been validated to `https:` by the domain schema
  // on the way in — so this cannot become `url(javascript:…)` however the row
  // was authored.
  const portrait = nonEmpty(branding.background_portrait_url);
  const landscape = nonEmpty(branding.background_landscape_url);
  set(root, '--brand-background-portrait', imageValue(portrait ?? landscape));
  set(root, '--brand-background-landscape', imageValue(landscape ?? portrait));

  // The primary buttons' fill (B9, "alive buttons"). `data-buttons` is the
  // hook `styles.css` keys on; the gradient's two stops and its label are
  // derived by `buttonGradient`, which steps the stops away from the label
  // until it clears AA at both — the button's own white first, the ink if
  // white would cost the brand more steps (a light gold). `animated` also sets
  // the gold heart's gradient drifting; `solid` is today's and writes only the
  // attribute.
  if (branding.buttons !== undefined) {
    root.dataset['buttons'] = branding.buttons;
    if (branding.buttons !== 'solid') {
      const gradient = buttonGradient({
        accent: nonEmpty(branding.accent) ?? DEFAULT_ACCENT,
        labels: ['#ffffff', INK],
      });
      set(root, '--button-fill-from', gradient?.from);
      set(root, '--button-fill-to', gradient?.to);
      set(root, '--button-fill-ink', gradient?.ink);
    }
  }

  // The hook `styles.css` hangs the background layer off. One attribute rather
  // than a rule per property, so an unbranded tenant's `body` has no
  // `::before` at all — not an empty one.
  root.dataset['branded'] = 'true';
}

function set(root: HTMLElement, property: string, value: string | undefined): void {
  if (value === undefined || value === '') return;
  root.style.setProperty(property, value);
}

function nonEmpty(value: string | undefined): string | undefined {
  return value === undefined || value === '' ? undefined : value;
}

function imageValue(url: string | undefined): string | undefined {
  return url === undefined ? undefined : `url("${url}")`;
}
